import type { InputFill } from "../../../api/content/authoring/processAuthoringApi";
import type { Selector, ValueKind } from "../../../api/content/authoring/selectorDecoders";
import {
  placementName,
  placementsAt,
  type ArtifactSummary,
  type BoundDefinitions,
  type NamedItem,
  type OutputPartReference,
  type ProcessCatalogue,
  type StepSummary,
  type ToolSummary,
  type WorkflowDefinition,
  type WorkflowDefinitionStep,
  type WorkflowStepStart,
} from "../../../api/content/processDefinitions";
import { nodePorts, portHandle } from "./graphPorts";
import { addSourceNodes, type SourceBuilder, type SourceItemKind, type SourceLevel } from "./graphSources";

/**
 * The Workflow view's graph, as plain data (processes.md P-14). The main pipeline is one
 * row of tool, agent and person steps, with sub-workflows as groups of steps on it (F61);
 * input steps above it, and the sources steps read, are the input band. Sub-workflows and
 * inputs stay collapsed until the user expands them, so the default view is the pipeline
 * and each opens to its actual complexity. React Flow only draws what this returns; positions come from `graphLayout`.
 *
 * Every edge runs from a named output to a named input (`graphPorts`): one edge per input
 * an earlier step's result fills (`step_result`), one per input a source or a prepared
 * input fills (`read`), and a thin `then` between two pipeline steps that follow each
 * other with nothing passed.
 *
 * Node IDs of steps are the runner's flattened keys (`<outer key>.<inner key>`, P-13), so a
 * Thread's step progress lands on the right node. Nodes inside an artifact's producing
 * Workflow belong to another Thread and carry no progress key.
 *
 * Steps are numbered by their place in their Workflow, in run order, and a sub-workflow's
 * steps within it: `1`, `1.1`, `2`. The list view and a Thread's step list use the same
 * numbers (`stepNumbers`). Steps of an artifact's producing Workflow are not numbered.
 */

/** Which row of its level a node sits in: the pipeline, or the inputs above it. */
export type GraphBand = "main" | "input";

export type PortSide = "top" | "left" | "right" | "bottom";

/** Whether a step needs a value for an input (W-4 `InputSlot.required`), as declared. */
export type PortNeed = "required" | "optional" | "undeclared";

/** A named input or output on a card (`graphPorts`). */
export interface GraphPort {
  /** The handle ID: `in:<slot>` or `out:<part>` (`portHandle`). */
  readonly id: string;
  readonly direction: "input" | "output";
  /** The slot, placeholder or part, as the definition names it. */
  readonly name: string;
  readonly side: PortSide;
  /** What it accepts or gives; empty where the definition does not declare it. */
  readonly kinds: readonly ValueKind[];
  /**
   * For an input: `required`, the step cannot run without a value; `optional`, it can;
   * `undeclared`, the definition does not list it (a step version that could not be read).
   * `null` for an output.
   */
  readonly need: PortNeed | null;
  /** An edge ends or starts here. An output without one is drawn hollow. */
  readonly connected: boolean;
  /** For an input: what fills it, as module B's check of the editor's draft says (F102 CP-04); `null` where no check says. */
  readonly filledBy: InputFill | null;
  /**
   * For an input a source fills, and a source's own output: the kind of item it reads or
   * gives (`sourceItemKind`), worded as the source's card words its type (F61), instead of
   * the value a selector renders.
   */
  readonly reads: SourceItemKind | null;
  /**
   * A binding may end or start here: a step's input slot or output part. A selector's
   * placeholder and a source's output are not bound by the Workflow.
   */
  readonly bindable: boolean;
}

/**
 * `allOutputs`: every part a step gives, also an unused `answer`, as the editor binds them;
 * `inputFills`: what fills the inputs of the shown Workflow's steps, by step key, as module
 * B's check of the editor's draft says.
 */
export interface GraphOptions {
  readonly allOutputs: boolean;
  readonly inputFills?: ReadonlyMap<string, Readonly<Record<string, InputFill>>> | null;
}

const READ_VIEW: GraphOptions = { allOutputs: false };

interface GraphNodeBase {
  readonly id: string;
  /** The expanded node it is drawn inside, or `null` at the top level. */
  readonly parentId: string | null;
  readonly band: GraphBand;
  /** 0 at the top level. */
  readonly depth: number;
  /** Inputs first, then outputs, each in the order the card shows them. */
  readonly ports: readonly GraphPort[];
}

export interface StepGraphNode extends GraphNodeBase {
  readonly kind: "step";
  readonly stepKey: string;
  /** The Workflow version this step belongs to: the one shown, a sub-workflow or a producer. */
  readonly inWorkflow: string;
  /** The key of the step in a Thread's progress, or `null` outside the Thread's Workflow. */
  readonly progressKey: string | null;
  /** Its place in the Workflow, such as `2` or `1.1`; `null` outside the Workflow shown. */
  readonly number: string | null;
  readonly start: WorkflowStepStart;
  readonly reference: string;
  /** `null` when the step version could not be read. */
  readonly step: StepSummary | null;
  /** For a tool step: the tool it runs; `null` when unknown. */
  readonly tool: ToolSummary | null;
  /** Its input bindings, as the Workflow sets them. */
  readonly bindings: Readonly<Record<string, OutputPartReference>>;
}

export interface SubWorkflowGraphNode extends GraphNodeBase {
  readonly kind: "sub_workflow";
  readonly stepKey: string;
  readonly inWorkflow: string;
  readonly progressKey: string | null;
  readonly number: string | null;
  readonly reference: string;
  /** `null` when the Workflow version could not be read. */
  readonly workflow: WorkflowDefinition | null;
  /** What the use binds to the sub-workflow's abstract steps (ADR-0043 D2). */
  readonly bound: BoundDefinitions;
  /** The names of its steps, in order; a step that could not be read by its key. */
  readonly stepNames: readonly string[];
  readonly expandable: boolean;
  readonly expanded: boolean;
}

/**
 * A place whose definition the Workflow that nests this one binds (ADR-0043 D1), drawn by its
 * label: in the Workflow that places it, or where a use leaves a required one unbound.
 */
export interface AbstractStepGraphNode extends GraphNodeBase {
  readonly kind: "abstract_step";
  readonly stepKey: string;
  readonly inWorkflow: string;
  /** Always `null`: nothing runs at an unbound abstract step. */
  readonly progressKey: null;
  readonly number: string | null;
  readonly start: WorkflowStepStart;
  readonly label: string;
  readonly description: string;
  /** Class IRIs of the definitions that may fill it. */
  readonly accepts: readonly string[];
  readonly required: boolean;
  /** Its input bindings, as the Workflow sets them. */
  readonly bindings: Readonly<Record<string, OutputPartReference>>;
  readonly expandable: false;
  readonly expanded: false;
}

/**
 * What every source shows on its input card (F61): its readable version (ADR-0033), where
 * the item has one.
 */
interface SourceNodeBase extends GraphNodeBase {
  /** Such as `1.2.0`; `null` without one, or for several items. */
  readonly version: string | null;
}

export interface ArtifactGraphNode extends SourceNodeBase {
  readonly kind: "artifact";
  /** The `targetKey` of the read: an item ID, a `VersionRef` or a role. */
  readonly item: string;
  readonly artifact: ArtifactSummary;
  /** The Workflow version that made the artifact, or that has a step proposing it. */
  readonly producer: WorkflowDefinition | null;
  /**
   * `made`: a run of `producer` made the version shown; `declared`: the version was made
   * by hand, and `producer` has a step that proposes it; `null` without a producer.
   */
  readonly producedBy: "made" | "declared" | null;
  readonly expandable: boolean;
  readonly expanded: boolean;
}

/** Selectors drawn as a source of their own: they pick items of a kind, not one artifact. */
export const SELECTION_KINDS = ["label_list", "competency_questions", "few_shot_examples"] as const;
export type SelectionKind = (typeof SELECTION_KINDS)[number];
export type SelectionSelector = Extract<Selector, { kind: SelectionKind }>;
export type TermsSelector = Extract<Selector, { kind: "ontology_terms" }>;

export interface SelectionGraphNode extends SourceNodeBase {
  readonly kind: "selection";
  /** The first read's selector: what the source is. */
  readonly selector: SelectionSelector;
  /** Every read of it at this level: a setting all of them share is the source's to show. */
  readonly reads: readonly SelectionSelector[];
  /** The item it names, such as the label list; `null` for one over the whole content version. */
  readonly item: NamedItem | null;
  /** How many labels or questions it holds, where known. */
  readonly count: number | null;
  readonly expandable: false;
  readonly expanded: false;
}

/** The ontology terms a step reads (`ontology_terms`), from the data model or some of its modules. */
export interface TermsGraphNode extends SourceNodeBase {
  readonly kind: "terms";
  /** The first read's selector: which terms. */
  readonly selector: TermsSelector;
  /** Every read of them at this level. */
  readonly reads: readonly TermsSelector[];
  /** The modules it names, by name where known; empty for the whole data model. */
  readonly modules: readonly string[];
  /** The item that names the annotation properties shown with each term, by name. */
  readonly propertiesFrom: string | null;
  readonly expandable: false;
  readonly expanded: false;
}

/** An item other than an artifact that an `item` selector names, such as one a role fills. */
export interface ItemGraphNode extends SourceNodeBase {
  readonly kind: "item";
  readonly item: NamedItem;
  readonly expandable: false;
  readonly expanded: false;
}

/** What the steps read from the project: the input band's cards (F61). */
export type SourceGraphNode = ArtifactGraphNode | SelectionGraphNode | TermsGraphNode | ItemGraphNode;

export type GraphNode = StepGraphNode | SubWorkflowGraphNode | AbstractStepGraphNode | SourceGraphNode;

/** A placement of a Workflow, rather than a source its steps read. */
export type PlacementGraphNode = StepGraphNode | SubWorkflowGraphNode | AbstractStepGraphNode;

export function isPlacementNode(node: GraphNode): node is PlacementGraphNode {
  return node.kind === "step" || node.kind === "sub_workflow" || node.kind === "abstract_step";
}

export function isSourceNode(node: GraphNode): node is SourceGraphNode {
  return !isPlacementNode(node);
}

/**
 * `step_result`: an earlier pipeline step's result, into the input it fills; `read`: read
 * from the project, or prepared beforehand by an input; `then`: the next pipeline step,
 * with nothing passed.
 */
export type GraphEdgeKind = "step_result" | "read" | "then";

export interface GraphEdge {
  readonly id: string;
  readonly kind: GraphEdgeKind;
  readonly source: string;
  /** The output it leaves from; `null` for `then`, which runs between the cards' sides. */
  readonly sourcePort: string | null;
  readonly target: string;
  /** The input it fills; `null` for `then`. */
  readonly targetPort: string | null;
  /**
   * `flow`: left to right along the pipeline, to the next step; `feed`: down from an input,
   * which sits above what it feeds; `skip`: under the pipeline, to a later step that is not
   * the next.
   */
  readonly route: "flow" | "feed" | "skip";
}

export interface WorkflowGraph {
  /** Parents before their children, as React Flow needs. */
  readonly nodes: readonly GraphNode[];
  readonly edges: readonly GraphEdge[];
}

/** Separates a sub-workflow's key from its steps' keys, as the runner flattens them (P-13). */
export const FLATTENED_KEY_SEPARATOR = ".";

interface Level extends SourceLevel {
  /** Prefix of the progress keys at this level, or `null` outside the Thread's Workflow. */
  readonly progressPrefix: string | null;
  /** Prefix of the step numbers at this level, or `null` where steps are not numbered. */
  readonly numberPrefix: string | null;
  /** What the use that nests this level binds to its abstract steps; `null` where nothing nests it (ADR-0043 D5). */
  readonly bound: BoundDefinitions | null;
}

/** Nodes are built without ports; `workflowGraph` adds them once every edge is known. */
interface GraphBuilder extends SourceBuilder {
  readonly edges: Map<string, GraphEdge>;
}

/** The node and handle that deliver a part of a step at one level: an inner step's once expanded. */
interface OutputEnd {
  readonly node: string;
  readonly port: string;
}

type OutputOf = (stepKey: string, part: string) => OutputEnd | null;

/**
 * The graph of `workflow`, with the nodes in `expanded` (node IDs) opened in place.
 * Unknown IDs in `expanded` are ignored, so a stored expansion survives new versions.
 */
export function workflowGraph(workflow: WorkflowDefinition, catalogue: ProcessCatalogue, expanded: ReadonlySet<string>, options: GraphOptions = READ_VIEW): WorkflowGraph {
  const edges = new Map<string, GraphEdge>();
  const builder: GraphBuilder = {
    catalogue,
    expanded,
    nodes: [],
    edges,
    addEdge: (edge) => {
      const id = `${edge.kind}:${edge.source}.${edge.sourcePort ?? ""}->${edge.target}.${edge.targetPort ?? ""}`;
      edges.set(id, { id, ...edge });
    },
    addProducerLevel: (producer, level) => {
      addLevel(builder, producer, { ...level, progressPrefix: null, numberPrefix: null, bound: null });
    },
  };
  addLevel(builder, workflow, { prefix: "", parentId: null, depth: 0, progressPrefix: "", numberPrefix: "", ancestry: new Set([workflow.reference]), bound: null });
  const allEdges = [...edges.values()];
  const fillOf = (node: GraphNode, slot: string) => (node.kind === "step" && node.inWorkflow === workflow.reference ? (options.inputFills?.get(node.stepKey)?.[slot] ?? null) : null);
  const ports = nodePorts(builder.nodes, allEdges, catalogue, options.allOutputs, fillOf);
  return { nodes: builder.nodes.map((node) => ({ ...node, ports: ports.get(node.id) ?? [] })), edges: allEdges };
}

/** The IDs of every node that can be expanded with everything else open: "expand all". */
export function expandableNodeIds(workflow: WorkflowDefinition, catalogue: ProcessCatalogue): ReadonlySet<string> {
  const opened = new Set<string>();
  for (;;) {
    const graph = workflowGraph(workflow, catalogue, opened);
    const closed = graph.nodes.filter((node) => isExpandable(node) && !node.expanded);
    if (closed.length === 0) {
      return opened;
    }
    for (const node of closed) {
      opened.add(node.id);
    }
  }
}

/** Opens in place: a sub-workflow, or an artifact with a producing Workflow. */
export function isExpandable(node: GraphNode): node is (SubWorkflowGraphNode | ArtifactGraphNode) & { readonly expandable: true } {
  return (node.kind === "sub_workflow" || node.kind === "artifact") && node.expandable;
}

/** Each step's number by its progress key, for a Thread's step list. */
export function stepNumbers(workflow: WorkflowDefinition, catalogue: ProcessCatalogue): ReadonlyMap<string, string> {
  const graph = workflowGraph(workflow, catalogue, expandableNodeIds(workflow, catalogue));
  return new Map(
    graph.nodes.flatMap((node): [string, string][] => (node.kind === "step" && node.progressKey !== null && node.number !== null ? [[node.progressKey, node.number]] : [])),
  );
}

/** The Workflow step key a node stands for; `null` for a source. */
export function stepKeyOf(node: GraphNode): string | null {
  return isPlacementNode(node) ? node.stepKey : null;
}

function addLevel(builder: GraphBuilder, workflow: WorkflowDefinition, level: Level): OutputOf {
  const placements = placementsAt(workflow, level.bound, builder.catalogue);
  const hasMainStep = placements.some((step) => step.role === "main");
  // A Workflow of input steps only is drawn as its own pipeline. A sub-workflow is a group of
  // steps on the main line, collapsed until opened, never an input card (F61).
  const bandOf = (step: WorkflowDefinitionStep): GraphBand => (!hasMainStep || step.role === "main" || step.kind === "workflow" ? "main" : "input");
  const outputs = new Map<string, (part: string) => OutputEnd>();

  placements.forEach((step, index) => {
    const number = level.numberPrefix === null ? null : `${level.numberPrefix}${String(index + 1)}`;
    outputs.set(step.key, addStepNode(builder, step, workflow.reference, bandOf(step), level, number));
  });
  addSourceNodes(builder, placements, level);

  const outputOf: OutputOf = (stepKey, part) => outputs.get(stepKey)?.(part) ?? null;
  const main = placements.filter((step) => bandOf(step) === "main");
  const mainIndex = new Map(main.map((step, index) => [step.key, index]));
  const stepsByKey = new Map(placements.map((step) => [step.key, step]));
  const passed = new Set<string>();
  for (const step of placements) {
    // A sub-workflow use's wires fill its Workflow's declared inputs, by name (ADR-0043 D3), and
    // its bindings' wires the open inputs of what it binds, `<abstract step>.<input>`: both name
    // steps of this level, so they are drawn here, into the use.
    for (const [slot, binding] of wiresHeldBy(step)) {
      const fromStep = stepsByKey.get(binding.fromStep);
      const source = outputOf(binding.fromStep, binding.part);
      if (fromStep === undefined || source === null) {
        continue;
      }
      const fromMain = bandOf(fromStep) === "main";
      const toMain = bandOf(step) === "main";
      const sourceIndex = mainIndex.get(fromStep.key) ?? -1;
      const targetIndex = mainIndex.get(step.key) ?? -1;
      const route = !fromMain ? "feed" : toMain && targetIndex !== sourceIndex + 1 ? "skip" : "flow";
      builder.addEdge({ kind: fromMain ? "step_result" : "read", source: source.node, sourcePort: source.port, target: level.prefix + step.key, targetPort: portHandle.input(slot), route });
      if (fromMain && toMain) {
        passed.add(`${fromStep.key}\n${step.key}`);
      }
    }
  }
  addThenEdges(builder, main, passed, level);
  return outputOf;
}

/** Every wire a placement's own entry holds, by the input it fills: a use's bindings' as `<abstract step>.<input>`. */
function wiresHeldBy(step: WorkflowDefinitionStep): [string, OutputPartReference][] {
  const own = Object.entries(step.inputBindings);
  if (step.kind !== "workflow") {
    return own;
  }
  return [...own, ...Object.entries(step.definitions).flatMap(([abstractStep, bound]) => Object.entries(bound.inputBindings).map(([input, wire]): [string, OutputPartReference] => [`${abstractStep}.${input}`, wire]))];
}

function addStepNode(builder: GraphBuilder, step: WorkflowDefinitionStep, inWorkflow: string, band: GraphBand, level: Level, number: string | null): (part: string) => OutputEnd {
  const id = level.prefix + step.key;
  const progressKey = level.progressPrefix === null ? null : level.progressPrefix + step.key;
  const common = { id, parentId: level.parentId, band, depth: level.depth, ports: [], stepKey: step.key, inWorkflow, progressKey, number };
  const own = (part: string): OutputEnd => ({ node: id, port: portHandle.output(part) });
  if (step.kind === "step") {
    const summary = builder.catalogue.steps.get(step.step) ?? null;
    const tool = summary?.tool == null ? null : (builder.catalogue.tools.get(summary.tool) ?? null);
    builder.nodes.push({ ...common, kind: "step", start: step.start, reference: step.step, step: summary, tool, bindings: step.inputBindings });
    return own;
  }
  if (step.kind === "abstract") {
    const { start, label, description, accepts, required, inputBindings: bindings } = step;
    builder.nodes.push({ ...common, kind: "abstract_step", progressKey: null, start, label, description, accepts, required, bindings, expandable: false, expanded: false });
    return own;
  }
  const inner = builder.catalogue.workflows.get(step.workflow) ?? null;
  const expandable = inner !== null && !level.ancestry.has(inner.reference);
  const expanded = expandable && builder.expanded.has(id);
  const stepNames = inner === null ? [] : placementsAt(inner, step.definitions, builder.catalogue).map((use) => placementName(use, builder.catalogue) ?? use.key);
  builder.nodes.push({ ...common, kind: "sub_workflow", reference: step.workflow, workflow: inner, bound: step.definitions, stepNames, expandable, expanded });
  if (inner === null || !expanded) {
    return own;
  }
  const innerOutputOf = addLevel(builder, inner, {
    prefix: id + FLATTENED_KEY_SEPARATOR,
    parentId: id,
    depth: level.depth + 1,
    progressPrefix: progressKey === null ? null : progressKey + FLATTENED_KEY_SEPARATOR,
    numberPrefix: number === null ? null : number + FLATTENED_KEY_SEPARATOR,
    ancestry: new Set([...level.ancestry, inner.reference]),
    bound: step.definitions,
  });
  return (part) => {
    const exported = inner.exports[part];
    return (exported === undefined ? null : innerOutputOf(exported.fromStep, exported.part)) ?? own(part);
  };
}

/** A thin arrow between two pipeline steps that follow each other with nothing passed. */
function addThenEdges(builder: GraphBuilder, main: readonly WorkflowDefinitionStep[], passed: ReadonlySet<string>, level: Level): void {
  main.forEach((step, index) => {
    const next = main[index + 1];
    if (next !== undefined && !passed.has(`${step.key}\n${next.key}`)) {
      builder.addEdge({ kind: "then", source: level.prefix + step.key, sourcePort: null, target: level.prefix + next.key, targetPort: null, route: "flow" });
    }
  });
}
